import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo2,
  Redo2,
  Code,
  Minus,
} from "lucide-react";

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
        active
          ? "bg-slate-900 text-white"
          : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichEditor({
  value,
  onChange,
  placeholder = "Write your mail-shot…",
  testId,
  minHeight = 260,
  insertMenu = null,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          style:
            "color:#0d7a75;text-decoration:underline;text-underline-offset:2px",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          style: "max-width:100%;border-radius:12px;margin:12px 0",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "data-testid": testId || "rich-editor",
        class:
          "prose prose-slate max-w-none focus:outline-none p-6 min-h-[240px]",
        style: `min-height:${minHeight}px;font-family:Georgia,serif;color:#1e293b;line-height:1.6`,
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep editor in sync when parent programmatically changes `value`
  // (e.g. "start from template" applied).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div
        className="border border-slate-200 rounded-xl bg-white"
        style={{ minHeight }}
      />
    );
  }

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to unlink)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <div className="flex items-center gap-1 border-b border-slate-100 px-2 py-1.5 flex-wrap bg-stone-50">
        <ToolbarBtn
          title="Heading 1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Heading 2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Inline code"
          active={editor.isActive("code")}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarBtn
          title="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Horizontal line"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <Minus className="w-4 h-4" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarBtn
          title="Insert link"
          active={editor.isActive("link")}
          onClick={setLink}
        >
          <LinkIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn title="Insert image" onClick={addImage}>
          <ImageIcon className="w-4 h-4" />
        </ToolbarBtn>
        <span className="flex-1" />
        <ToolbarBtn
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      {insertMenu && insertMenu.length > 0 && (
        <div className="flex items-center gap-1.5 border-b border-slate-100 px-2 py-1.5 flex-wrap bg-white">
          <span className="text-[10px] tracking-widest uppercase text-slate-400 mr-1">Insert</span>
          {insertMenu.map((item) => (
            <button
              key={item.label}
              type="button"
              data-testid={`insert-${item.testid || item.label.toLowerCase().replace(/\s+/g, "-")}`}
              title={item.title || `Insert ${item.label}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() =>
                editor.chain().focus().insertContent(item.value).run()
              }
              className="text-[11px] tracking-wide px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-teal-600 hover:text-teal-700 font-mono"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
